import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap";
import { Trash2 } from "react-feather";
import { toast } from "react-toastify";
import { roomTypeService } from "../../services/roomTypeService";
import ConfirmDeleteDialog from "../Common/ConfirmDeleteDialog";

/**
 * RoomTypeModal - Modal for creating, viewing, editing, and deleting room types
 */
const RoomTypeModal = ({ isOpen, toggle, mode = "create", roomTypeData = null, onSuccess }) => {
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setConfirmingDelete(false);
    setErrors({});
    if (mode === "create") {
      setFormData({ name: "", description: "" });
    } else if ((mode === "view" || mode === "edit") && roomTypeData) {
      setFormData({
        name: roomTypeData.name || "",
        description: roomTypeData.description || "",
      });
    }
  }, [isOpen, mode, roomTypeData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Room type name is required";
    } else if (formData.name.length < 2) {
      newErrors.name = "Room type name must be at least 2 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === "view") { toggle(); return; }
    if (!validateForm()) { toast.error("Please fix the form errors"); return; }

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      let response;
      if (mode === "create") {
        response = await roomTypeService.create(payload);
        toast.success("Room type created successfully");
      } else if (mode === "edit") {
        response = await roomTypeService.update(roomTypeData.id, payload);
        toast.success("Room type updated successfully");
      }
      if (onSuccess) onSuccess(response);
      toggle();
    } catch (error) {
      console.error(`Error ${mode === "create" ? "creating" : "updating"} room type:`, error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to ${mode === "create" ? "create" : "update"} room type`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!roomTypeData) return;
    setDeleting(true);
    try {
      await roomTypeService.remove(roomTypeData.id);
      toast.success("Room type deleted successfully");
      if (onSuccess) onSuccess();
      toggle();
    } catch (error) {
      if (error.response?.data?.message) toast.error(error.response.data.message);
      else toast.error("Failed to delete room type");
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create": return "Create New Room Type";
      case "edit":   return "Edit Room Type";
      case "view":   return "Room Type Details";
      default:       return "Room Type";
    }
  };

  return (
    <>
      <Modal isOpen={isOpen && !confirmingDelete} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>{getModalTitle()}</ModalHeader>
        <>
          <ModalBody>

            {/* ── CREATE / EDIT ───────────────────────────────────────────── */}
            {(mode === "create" || mode === "edit") && (
              <>
                <FormGroup>
                  <Label for="rtName">
                    Room Type Name <span className="text-danger">*</span>
                  </Label>
                  <Input
                    id="rtName"
                    type="text"
                    placeholder="Enter room type name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    invalid={!!errors.name}
                    disabled={loading}
                  />
                  <FormFeedback>{errors.name}</FormFeedback>
                </FormGroup>

                <FormGroup>
                  <Label for="rtDescription">Description</Label>
                  <Input
                    id="rtDescription"
                    type="textarea"
                    placeholder="Enter description"
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    rows="3"
                    disabled={loading}
                  />
                </FormGroup>
              </>
            )}

            {/* ── VIEW ───────────────────────────────────────────────────── */}
            {mode === "view" && roomTypeData && (
              <>
                <h6 className="mb-3">Room Type Details</h6>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>ID</Label>
                      <Input type="text" value={roomTypeData.id || "-"} disabled />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Name</Label>
                      <Input type="text" value={roomTypeData.name || "-"} disabled />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        type="textarea"
                        value={roomTypeData.description || "-"}
                        disabled
                        rows="3"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Created At</Label>
                      <Input
                        type="text"
                        value={roomTypeData.createdAt ? new Date(roomTypeData.createdAt).toLocaleString() : "-"}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Updated At</Label>
                      <Input
                        type="text"
                        value={roomTypeData.updatedAt ? new Date(roomTypeData.updatedAt).toLocaleString() : "-"}
                        disabled
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}
          </ModalBody>

          <ModalFooter>
            {mode === "view" ? (
              <>
                <Button
                  type="button"
                  color="danger"
                  outline
                  onClick={() => setConfirmingDelete(true)}
                  className="me-auto"
                >
                  <Trash2 size={14} className="me-1" />
                  Delete
                </Button>
                <Button type="button" color="secondary" onClick={toggle}>
                  Close
                </Button>
              </>
            ) : (
              <>
                <Button type="button" color="secondary" onClick={toggle} disabled={loading}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2" />
                      {mode === "create" ? "Creating..." : "Updating..."}</>
                  ) : (
                    mode === "create" ? "Create Room Type" : "Update Room Type"
                  )}
                </Button>
              </>
            )}
          </ModalFooter>
        </>
      </Modal>

      <ConfirmDeleteDialog
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={handleDelete}
        deleting={deleting}
        title="Delete Room Type"
        itemName={roomTypeData?.name}
        warningText="Rooms using this type may be affected."
      />
    </>
  );
};

export default RoomTypeModal;
